import { getDB } from "./schema.js";
import { syncFollows, FOLLOWS_CHANGED } from "./cloud-follows.js";
import { nowIso } from "../utils/dates.js";
import { getSession, onAuthChange } from "./auth.js";
import { isMigrationDone } from "./migration.js";
import {
	toCloudRow,
	fromCloudRow,
	upsertCloudRow,
	deleteCloudItem,
	fetchCloudItems,
	externalIdOf,
	sourceOf,
} from "./cloud-items.js";
import {
	toCloudGroup,
	fromCloudGroup,
	upsertCloudGroup,
	deleteCloudGroup,
	fetchCloudGroups,
} from "./cloud-groups.js";
import {
	toCloudHistory,
	fromCloudHistory,
	upsertCloudHistory,
	fetchCloudHistorySince,
	historyWatermarkKey,
} from "./cloud-history.js";

// Cloud sync engine. Phase 4: optimistic local writes (done in games.js/books.js)
// are mirrored to Supabase through an outbox (the `pending_operations` store) and
// the cloud is pulled back on launch/focus. All of it is a no-op when signed out,
// so the app stays fully local-first without an account.
//
// Safety posture (Phase 5 migration not built yet): refresh only ADDS/updates
// from the cloud and only deletes local records that are already cloud-backed
// (`_synced`) and have vanished server-side. Local-only records are never touched.

const PENDING = "pending_operations";
const MAX_ATTEMPTS = 5;
const DRAIN_DEBOUNCE_MS = 300;
const INTERVAL_MS = 30000;

// Map a media type to its IndexedDB store + external-id index.
const STORE_BY_MEDIA = {
	videogame: "games",
	book: "books",
	tv: "tv",
	anime: "anime",
	movie: "movies",
	comic: "comics",
};
const INDEX_BY_MEDIA = {
	videogame: "igdb_id",
	book: "ol_id",
	tv: "tvmaze_id",
	anime: "tvmaze_id",
	movie: "imdb_id",
	comic: "comic_key",
};
// Media types whose external id is a STRING locally (Open Library "OL…W", IMDb
// "tt…"); every other source id is numeric and the cloud's text column must be
// converted back before an index lookup.
const STRING_ID_MEDIA = new Set(["book", "movie", "comic"]);
// External-id field per synced store (for the cross-device delete pass).
const EXT_KEY_BY_STORE = {
	games: "igdb_id",
	books: "ol_id",
	tv: "tvmaze_id",
	anime: "tvmaze_id",
	movies: "imdb_id",
	comics: "comic_key",
};
const SYNCED_STORES = Object.keys(EXT_KEY_BY_STORE);

let currentUserId = null;
let started = false;
let draining = false;
let refreshing = false;
let intervalId = null;
let drainTimer = null;
let refreshView = () => {};

function uuid() {
	if (globalThis.crypto?.randomUUID) return crypto.randomUUID();
	return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
		const r = (Math.random() * 16) | 0;
		const v = c === "x" ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
}

// main.js registers a hook used to re-render the current view after a pull that
// changed the cache (kept here to avoid importing the router into the db layer).
export function setRefreshView(fn) {
	refreshView = typeof fn === "function" ? fn : () => {};
}

// ---------- outbox ----------

// Called by every store mutation. No-op when signed out (local-only mode).
//
// The local write has ALREADY happened by the time this runs; enqueuing to the
// outbox is best-effort bookkeeping and must never throw back into the caller, or
// a sync hiccup would surface as "couldn't add/save". On failure we log and move
// on — the periodic refresh and a future full re-sync reconcile any missed op.
export async function queueCloudWrite({ type, mediaType, record }) {
	if (!currentUserId) return;
	try {
		const db = await getDB();
		await db.add(PENDING, {
			id: uuid(),
			type,
			media_type: mediaType,
			source: sourceOf(mediaType, record),
			external_id: externalIdOf(record, mediaType),
			record_id: record.id,
			payload: type === "delete" ? null : record,
			created_at: nowIso(),
			attempts: 0,
			last_error: null,
			failed: false,
		});
		scheduleDrain();
		emitSyncState();
	} catch (err) {
		console.error("queueCloudWrite failed (local write kept):", err);
	}
}

// Groups outbox. Groups aren't items (no source/external_id) — they're keyed by
// their own uuid — so they ride the same `pending_operations` store tagged with
// `entity: "group"`. No-op when signed out, same as queueCloudWrite. Called by
// every group mutation in db/groups.js.
export async function queueGroupWrite({ type, record }) {
	if (!currentUserId) return;
	try {
		const db = await getDB();
		await db.add(PENDING, {
			id: uuid(),
			entity: "group",
			type,
			record_id: record.id,
			payload: type === "delete" ? null : record,
			created_at: nowIso(),
			attempts: 0,
			last_error: null,
			failed: false,
		});
		scheduleDrain();
	} catch (err) {
		console.error("queueGroupWrite failed (local write kept):", err);
	}
}

// History outbox (design_documents/history.md). Events are keyed by their own
// uuid and never hard-deleted (removal = a `deleted` tombstone), so every op is
// an upsert. No-op when signed out — unsynced events are pushed in one batch on
// the next sign-in (pushUnsyncedHistory).
export async function queueHistoryWrite(record) {
	if (!currentUserId) return;
	try {
		const db = await getDB();
		await db.add(PENDING, {
			id: uuid(),
			entity: "history",
			type: "upsert",
			record_id: record.id,
			payload: record,
			created_at: nowIso(),
			attempts: 0,
			last_error: null,
			failed: false,
		});
		scheduleDrain();
	} catch (err) {
		console.warn("queueHistoryWrite failed (local event kept):", err);
	}
}

// ---------- sync status (roadmap #23: the top-bar dot + Account → Sync) ----------

export const SYNC_EVENT = "sync:state";
let lastSyncAt = null;

function emitSyncState() {
	try {
		window.dispatchEvent(new Event(SYNC_EVENT));
	} catch {
		// no window (tests)
	}
}

// { signedIn, online, pending, failed, lastSyncAt } — pending = ops waiting to
// upload, failed = ops that gave up after MAX_ATTEMPTS.
export async function getSyncState() {
	const db = await getDB();
	const ops = await db.getAll(PENDING);
	return {
		signedIn: !!currentUserId,
		online: typeof navigator === "undefined" ? true : navigator.onLine !== false,
		pending: ops.filter((o) => !o.failed).length,
		failed: ops.filter((o) => o.failed).length,
		lastSyncAt,
	};
}

// Give failed ops another go (Account → Sync → Retry).
export async function retryFailedOps() {
	const db = await getDB();
	for (const op of await db.getAll(PENDING)) {
		if (op.failed) await db.put(PENDING, { ...op, failed: false, attempts: 0 });
	}
	emitSyncState();
	await drainQueue();
}

// "Sync now": push what's waiting, then pull.
export async function syncNow() {
	await drainQueue();
	const changed = await refreshFromCloud();
	await pushUnsyncedHistory();
	return changed;
}

function scheduleDrain() {
	clearTimeout(drainTimer);
	drainTimer = setTimeout(() => drainQueue(), DRAIN_DEBOUNCE_MS);
}

// Push queued ops to Supabase, oldest first. On a transient failure we stop to
// preserve order and retry on the next trigger; an op that fails MAX_ATTEMPTS
// times is flagged `failed` and skipped so it can't wedge the queue.
export async function drainQueue() {
	if (!currentUserId || !navigator.onLine || draining) return;
	draining = true;
	try {
		const db = await getDB();
		const ops = await db.getAllFromIndex(PENDING, "created_at");
		for (const op of ops) {
			if (op.failed) continue;
			try {
				if (op.entity === "history") {
					await upsertCloudHistory(toCloudHistory(op.payload, currentUserId));
					await db.delete(PENDING, op.id);
					await stampHistorySynced(op.payload);
				} else if ((op.entity ?? "item") === "group") {
					if (op.type === "delete") {
						await deleteCloudGroup(currentUserId, op.record_id);
					} else {
						await upsertCloudGroup(toCloudGroup(op.payload, currentUserId));
					}
					await db.delete(PENDING, op.id);
					if (op.type !== "delete") await stampGroupSynced(op.record_id);
				} else if (op.type === "delete") {
					await deleteCloudItem(currentUserId, op.source, op.external_id);
					await db.delete(PENDING, op.id);
				} else {
					await upsertCloudRow(
						toCloudRow(op.payload, op.media_type, currentUserId),
					);
					await db.delete(PENDING, op.id);
					await stampSynced(op.media_type, op.record_id);
				}
			} catch (err) {
				op.attempts = (op.attempts ?? 0) + 1;
				op.last_error = String(err?.message ?? err);
				if (op.attempts >= MAX_ATTEMPTS) op.failed = true;
				await db.put(PENDING, op);
				const label =
					(op.entity ?? "item") !== "item"
						? `${op.entity}:${op.record_id}`
						: `${op.source}:${op.external_id}`;
				console.warn(
					`Sync push ${op.type} ${label} failed ` +
						`(attempt ${op.attempts}${op.failed ? ", giving up" : ""}):`,
					err,
				);
				// Transient: stop and retry later (keep order). Permanent: skip on.
				if (!op.failed) break;
			}
		}
	} catch (err) {
		// Fire-and-forget from several triggers — never surface as an uncaught
		// rejection. Per-op errors are handled above; this guards the queue read.
		console.warn("drainQueue aborted:", err);
	} finally {
		draining = false;
		lastSyncAt = new Date().toISOString();
		emitSyncState();
	}
}

// Mark a local record cloud-backed after a successful push. Direct put — does NOT
// bump updated_at and does NOT re-enqueue (it bypasses the store mutations).
async function stampSynced(mediaType, recordId) {
	const store = STORE_BY_MEDIA[mediaType];
	const db = await getDB();
	const rec = await db.get(store, recordId);
	if (rec && !rec._synced) {
		rec._synced = true;
		await db.put(store, rec);
	}
}

// Stamp an event synced — unless it changed again after this op was queued.
async function stampHistorySynced(payload) {
	const db = await getDB();
	const rec = await db.get(HISTORY, payload.id);
	if (rec && !rec._synced && rec.updated_at === payload.updated_at) {
		rec._synced = true;
		await db.put(HISTORY, rec);
	}
}

const HISTORY = "history";
const HISTORY_PUSH_CHUNK = 500;

// Push every local event not yet cloud-backed in batches (the reconstructed
// past can be hundreds of events at once — far too many for one-op-per-event).
// Gated on a completed migration, like groups.
export async function pushUnsyncedHistory() {
	if (!currentUserId || !isMigrationDone()) return;
	try {
		const db = await getDB();
		const pendingIds = new Set(
			// Live ops only: one that gave up (e.g. before the table existed) must not
			// keep its event from ever syncing — the batch push retries it.
			(await db.getAll(PENDING)).filter((o) => o.entity === "history" && !o.failed).map((o) => o.record_id),
		);
		const unsynced = (await db.getAll(HISTORY)).filter((e) => !e._synced && !pendingIds.has(e.id));
		for (let i = 0; i < unsynced.length; i += HISTORY_PUSH_CHUNK) {
			const chunk = unsynced.slice(i, i + HISTORY_PUSH_CHUNK);
			await upsertCloudHistory(chunk.map((e) => toCloudHistory(e, currentUserId)));
			for (const e of chunk) {
				const rec = await db.get(HISTORY, e.id);
				if (rec && rec.updated_at === e.updated_at) await db.put(HISTORY, { ...rec, _synced: true });
			}
		}
	} catch (err) {
		console.warn("History push failed (will retry):", err?.message ?? err);
	}
}

// Incremental pull: rows whose server-stamped synced_at is newer than what this
// device last saw (per user). Events with an un-pushed local change win.
async function pullHistory(db, pendingOps) {
	let since = null;
	try {
		since = localStorage.getItem(historyWatermarkKey(currentUserId));
	} catch {
		// no storage — pull everything
	}
	const { rows, newest } = await fetchCloudHistorySince(currentUserId, since);
	const pendingIds = new Set(pendingOps.filter((o) => o.entity === "history").map((o) => o.record_id));
	let changed = false;
	for (const row of rows) {
		if (pendingIds.has(row.id)) continue;
		await db.put(HISTORY, fromCloudHistory(row));
		changed = true;
	}
	if (newest) {
		try {
			localStorage.setItem(historyWatermarkKey(currentUserId), newest);
		} catch {
			// fine — next pull starts over
		}
	}
	return changed;
}

async function stampGroupSynced(id) {
	const db = await getDB();
	const rec = await db.get("groups", id);
	if (rec && !rec._synced) {
		rec._synced = true;
		await db.put("groups", rec);
	}
}

// Push any local group not yet cloud-backed (e.g. groups built before this
// device synced groups, or while offline). Idempotent: a stamped group is
// skipped, so after the first successful drain this enqueues nothing. Gated on a
// completed migration so it can't pre-empt the first-signup "move vs discard"
// choice (the migrate flow pushes groups on "move", clears them on "discard").
async function pushUnsyncedGroups() {
	if (!currentUserId || !isMigrationDone()) return;
	const db = await getDB();
	for (const g of await db.getAll("groups")) {
		if (!g._synced) await queueGroupWrite({ type: "update", record: g });
	}
}

// ---------- pull / refresh ----------

// Pull the user's cloud items and reconcile into the local cache. Matching is by
// (source, external_id) via the per-store unique index, so the index never
// collides. Server-wins by updated_at; records with a pending op are left alone.
export async function refreshFromCloud() {
	if (!currentUserId || !navigator.onLine || refreshing) return false;
	refreshing = true;
	try {
		const db = await getDB();
		const rows = await fetchCloudItems(currentUserId);

		const pendingOps = await db.getAll(PENDING);
		const pendingRecordIds = new Set(pendingOps.map((o) => o.record_id));

		// Cloud external-id sets per store, for the delete pass below.
		const cloudExt = Object.fromEntries(
			SYNCED_STORES.map((store) => [store, new Set()]),
		);
		let changed = false;

		for (const row of rows) {
			const store = STORE_BY_MEDIA[row.media_type];
			if (!store) continue; // unknown media type (future) — ignore
			// Comics have two sources, so their local key is "<source>:<external_id>".
			const extKey =
				row.media_type === "comic"
					? `${row.source}:${row.external_id}`
					: String(row.external_id);
			cloudExt[store].add(extKey);

			const indexName = INDEX_BY_MEDIA[row.media_type];
			const indexValue = STRING_ID_MEDIA.has(row.media_type)
				? extKey
				: Number(row.external_id);
			const local = await db.getFromIndex(store, indexName, indexValue);
			const incoming = fromCloudRow(row);

			if (!local) {
				await db.put(store, incoming);
				changed = true;
				continue;
			}
			if (pendingRecordIds.has(local.id)) continue; // un-pushed local edit wins
			if (isNewer(incoming.updated_at, local.updated_at)) {
				// Keep the existing local id so the unique index can't collide.
				await db.put(store, { ...incoming, id: local.id });
				changed = true;
			} else if (!local._synced) {
				// Same/older server copy, but confirm it's cloud-backed so a future
				// remote delete can propagate.
				await db.put(store, { ...local, _synced: true });
			}
		}

		// Cross-device delete: drop local records that were cloud-backed but are
		// now gone server-side. Never touch un-pushed local-only records.
		for (const store of SYNCED_STORES) {
			const extKey = EXT_KEY_BY_STORE[store];
			const all = await db.getAll(store);
			for (const rec of all) {
				if (!rec._synced) continue;
				if (pendingRecordIds.has(rec.id)) continue;
				if (!cloudExt[store].has(String(rec[extKey]))) {
					await db.delete(store, rec.id);
					changed = true;
				}
			}
		}

		// ---- groups ----
		// Groups match by their own uuid (no external index). Same server-wins-by-
		// updated_at policy, same "un-pushed local edit wins" guard, same cross-
		// device delete of cloud-backed groups that vanished server-side.
		const cloudGroups = await fetchCloudGroups(currentUserId);
		const pendingGroupIds = new Set(
			pendingOps
				.filter((o) => (o.entity ?? "item") === "group")
				.map((o) => o.record_id),
		);
		const cloudGroupIds = new Set();
		for (const row of cloudGroups) {
			cloudGroupIds.add(row.id);
			const local = await db.get("groups", row.id);
			const incoming = fromCloudGroup(row);
			if (!local) {
				await db.put("groups", incoming);
				changed = true;
				continue;
			}
			if (pendingGroupIds.has(local.id)) continue; // un-pushed local edit wins
			if (isNewer(incoming.updated_at, local.updated_at)) {
				await db.put("groups", incoming);
				changed = true;
			} else if (!local._synced) {
				await db.put("groups", { ...local, _synced: true });
			}
		}
		for (const g of await db.getAll("groups")) {
			if (!g._synced) continue;
			if (pendingGroupIds.has(g.id)) continue;
			if (!cloudGroupIds.has(g.id)) {
				await db.delete("groups", g.id);
				changed = true;
			}
		}

		// ---- history ----
		try {
			if (await pullHistory(db, pendingOps)) changed = true;
		} catch (err) {
			console.warn("History pull failed:", err?.message ?? err);
		}

		// ---- follows + subscriptions (snapshot reconcile, db/cloud-follows.js) ----
		try {
			if (await syncFollows(currentUserId)) changed = true;
		} catch (err) {
			console.warn("Follows sync failed:", err?.message ?? err);
		}

		return changed;
	} catch (err) {
		console.warn("Cloud refresh failed:", err?.message ?? err);
		return false;
	} finally {
		refreshing = false;
		emitSyncState();
	}
}

function isNewer(a, b) {
	if (!a) return false;
	if (!b) return true;
	return new Date(a).getTime() > new Date(b).getTime();
}

// ---------- lifecycle ----------

export async function initSync() {
	if (started) return;
	started = true;
	// A follow / unfollow / subscription change: push it shortly.
	let followsTimer = null;
	window.addEventListener(FOLLOWS_CHANGED, () => {
		clearTimeout(followsTimer);
		followsTimer = setTimeout(() => {
			if (currentUserId && navigator.onLine) syncFollows(currentUserId).catch((err) => console.warn("Follows sync failed:", err?.message ?? err));
		}, 2000);
	});

	const session = await getSession();
	currentUserId = session?.user?.id ?? null;

	onAuthChange((_event, s) => {
		const uid = s?.user?.id ?? null;
		if (uid && uid !== currentUserId) {
			currentUserId = uid;
			onSignedIn();
		} else if (!uid && currentUserId) {
			currentUserId = null;
			stopTimers();
		}
	});

	window.addEventListener("online", () => drainQueue());
	window.addEventListener("focus", onForeground);
	document.addEventListener("visibilitychange", () => {
		if (document.visibilityState === "visible") onForeground();
	});

	if (currentUserId) {
		startInterval();
		const changed = await refreshFromCloud();
		if (changed) refreshView();
		await pushUnsyncedGroups();
		await pushUnsyncedHistory();
		drainQueue();
	}
}

async function onSignedIn() {
	startInterval();
	const changed = await refreshFromCloud();
	if (changed) refreshView();
	await pushUnsyncedGroups();
	await pushUnsyncedHistory();
	drainQueue();
}

// Focus/visibility: drain pending writes and refresh the cache silently. We don't
// force a re-render here (it would reset scroll); the next navigation shows the
// fresh cache.
function onForeground() {
	if (!currentUserId || !navigator.onLine) return;
	drainQueue();
	refreshFromCloud();
}

function startInterval() {
	if (intervalId) return;
	intervalId = setInterval(() => {
		if (navigator.onLine) drainQueue();
	}, INTERVAL_MS);
}

function stopTimers() {
	if (intervalId) {
		clearInterval(intervalId);
		intervalId = null;
	}
}
